import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KeinEinsatzLeer } from '../components/LeerZustand'
import { MapContainer, TileLayer, Marker, Polygon, Tooltip, ZoomControl, useMapEvents, useMap } from 'react-leaflet'
import { Input, useEinsatz, useEinsatzWechsel, useConnectionRetry, useDatenFehler } from '@pilot-hub/ui'
import { api, vehicleApi } from '@pilot-hub/api-client'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { MessLayer, MessPanelInhalt, messenAktiv, useMessen, geodaesie } from '@pilot-hub/ui/components/karte/index'
import {
  Trash2, X, Map as MapIcon, Layers, Crosshair, Ruler,
} from 'lucide-react'
import { SYMBOL_KATALOG, symbolDef, symbolSvg, symbolLabel, type Organisation } from '../components/taktischeSymbole'
import { KartenBuehne, type KartenPanelDef } from '../components/KartenPanel'
import { SymbolInfoKarte, SymbolTooltip, hatMaus, symbolIconHtml, type TaktikZeichen, truppsAuswerten, fahrzeugRufnamenJeNutzer, truppZeichen, truppAnzeige } from '../components/taktischeSymbolUi'
import { useDrohnenLive, DrohnenLayer, DrohnenPanel, FlugroutenLayer } from '../components/DrohnenLive'
import { PilotMarkierungenLayer, usePilotMarkierungen } from '../components/PilotMarkierungen'
import { AbdeckungLayer, AbdeckungHinweis, ABDECKUNG_STANDARD_HOEHE_M, abdeckungProzent, useAbdeckung } from '../components/Abdeckung'
import { FotoLayer, HotspotLayer, MedienStatusHinweis, useFotos, useHotspots, useMedienStatus, type FotoFilter } from '../components/Lagebild'

/** Icons je Inhalt wiederverwenden: ein neues Icon-Objekt lässt react-leaflet setIcon() aufrufen,
 *  Leaflet ersetzt dann das Element und bricht ein laufendes Ziehen ab (v. a. per Touch am iPad,
 *  da die Karte alle 2 s die Drohnen neu lädt und dabei neu rendert). */
const iconCache = new Map<string, L.DivIcon>()
function divIcon(opts: L.DivIconOptions): L.DivIcon {
  const key = JSON.stringify(opts)
  let i = iconCache.get(key)
  if (!i) { i = L.divIcon(opts); iconCache.set(key, i) }
  return i
}

/* ------------------------------------------------------------------ Typen */

interface EinsatzOption {
  id: number
  titel: string
  status: string
  typ?: string | null
  einsatzort_lat?: number | null
  einsatzort_lon?: number | null
  einsatzort_adresse?: string | null
  beginn_geplant?: string | null
}

interface Fahrzeug { id: number; rufzeichen?: string | null; fahrzeugtyp?: string | null; kennzeichen?: string | null; typ?: string | null }

interface KartenSymbol {
  id: string
  typ: string
  lat: number
  lng: number
  kennung: string
  /** Fahrzeugtyp o. Ä. – Zusatzinfo, z. B. "GW-Drohne" */
  bezeichnung?: string
  /** Funkrufname, unter dem Zeichen angezeigt */
  rufname?: string
  /** Farbe nach Organisation; leer = Standard des Zeichens */
  organisation?: Organisation
  /** Trupp aus dem Kräfte-Management, aus dem das Zeichen gesetzt wurde */
  truppKey?: string
}

interface Suchgebiet {
  id: string
  kennung: string
  punkte: [number, number][]
}

type Grundkarte = 'karte' | 'satellit' | 'topo'

interface LayerSicht {
  /** Hintergrundkarte: Straßenkarte, Luftbild oder topografisch mit Höhenlinien */
  grundkarte: Grundkarte
  einsatzort: boolean
  symbole: boolean
  suchgebiete: boolean
  /** Rufnamen unter den Zeichen anzeigen */
  rufnamen: boolean
  /** Bisherige Flugroute der Drohnen im aktuellen Flug */
  flugrouten: boolean
  /** Markierungen, die der Pilot in DJI Pilot 2 zeichnet (fehlt in älteren Karten = an) */
  pilotmarker?: boolean
  /** Abdeckungsnachweis der Suchgebiete (Standard: aus); die Höhe stellt die Führung ein */
  abdeckung?: boolean
  abdeckungHoehe?: number
  /** Foto-Lagebild (Fotos aus DJI Pilot 2) und Hotspots aus IR-Fotos (Standard: aus) */
  fotos?: boolean
  hotspots?: boolean
  /** Master-Schalter für die DIPUL-Geozonen (§21h LuftVO) */
  luftraum: boolean
  /** Einzelne Geozonen-Layer */
  geozonen: Record<string, boolean>
}

interface KarteData {
  layer?: LayerSicht
  symbole?: KartenSymbol[]
  suchgebiete?: Suchgebiet[]
}

const GRUNDKARTEN: Array<{ key: Grundkarte; label: string; url: string; attribution: string; maxNativeZoom: number }> = [
  {
    key: 'karte', label: 'Karte',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxNativeZoom: 19,
  },
  {
    key: 'satellit', label: 'Satellit',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Luftbild: &copy; Esri, Maxar, Earthstar Geographics',
    maxNativeZoom: 19,
  },
  {
    key: 'topo', label: 'Höhenlinien',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Kartendaten: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxNativeZoom: 17,
  },
]

const LEERE_LAYER: LayerSicht = {
  grundkarte: 'karte', einsatzort: true, symbole: true, suchgebiete: true, rufnamen: true, flugrouten: true, luftraum: false,
  geozonen: {},
}

/* -------------------------------------------------------------------- DIPUL
 * Offizielle Geozonen nach §21h LuftVO – kostenfrei über OGC-Schnittstellen
 * der DFS/DIPUL bereitgestellt. Geprüft: WMS 1.3.0 (EPSG:3857) und
 * WFS 2.0.0 (bbox in lon,lat-Reihenfolge!) liefern beide HTTP 200, CORS offen.
 */
const DIPUL_OWS = 'https://uas-betrieb.de/geoservices/dipul/ows'

const GEOZONEN: Array<{ key: string; layer: string; label: string; farbe: string }> = [
  { key: 'flugbeschraenkung', layer: 'flugbeschraenkungsgebiete',           label: 'Flugbeschränkungsgebiete',          farbe: '#dc2626' },
  { key: 'temporaer',         layer: 'temporaere_betriebseinschraenkungen', label: 'Temporäre Einschränkungen (NOTAM)', farbe: '#f59e0b' },
  { key: 'kontrollzone',      layer: 'kontrollzonen',                       label: 'Kontrollzonen',                     farbe: '#2563eb' },
  { key: 'flughafen',         layer: 'flughaefen',                          label: 'Flughäfen',                         farbe: '#7c3aed' },
  { key: 'flugplatz',         layer: 'flugplaetze',                         label: 'Flugplätze',                        farbe: '#0891b2' },
  { key: 'militaer',          layer: 'militaerische_anlagen',               label: 'Militärische Anlagen',              farbe: '#4b5563' },
  { key: 'naturschutz',       layer: 'naturschutzgebiete',                  label: 'Naturschutzgebiete',                farbe: '#15803d' },
]

/**
 * DIPUL-Geozone als WMS-Overlay.
 * Wichtig: Leaflets einfacher TileLayer kennt `{bbox-epsg-3857}` NICHT — nur
 * `L.tileLayer.wms` baut die bbox korrekt. Version 1.1.1, damit die bbox in
 * x,y-Reihenfolge gesendet wird (verifiziert: HTTP 200, echtes PNG).
 */
function WmsGeozon({ layer, opacity = 0.75 }: { layer: string; opacity?: number }) {
  const map = useMap()
  useEffect(() => {
    const wms = L.tileLayer.wms(DIPUL_OWS, {
      layers: layer,
      styles: '',
      format: 'image/png',
      transparent: true,
      version: '1.1.1',
      opacity,
      attribution: GEOZON_ATTRIBUTION,
      crossOrigin: true,
    })
    wms.addTo(map)
    return () => { map.removeLayer(wms) }
  }, [map, layer, opacity])
  return null
}

const GEOZON_ATTRIBUTION =
  'Geozonen: &copy; DFS/DIPUL – §21h LuftVO (kostenfrei)'

interface GeozonTreffer {
  zone: string
  farbe: string
  name: string
  kategorie: string
  rechtsgrundlage: string
  untergrenze: string
  obergrenze: string
  gueltigkeit: string
}

let zaehler = 0
const neueId = (p: string) => `${p}-${Date.now().toString(36)}-${++zaehler}`

/* ------------------------------------------------------- Karte: Maushilfen */

/**
 * Zentriert die Karte auf den Einsatzort – auch wenn der Einsatz später
 * gewechselt wird (MapContainer wertet `center` nur beim ersten Rendern aus).
 * Zusätzlich invalidateSize, damit die Kacheln nach dem Aufbau korrekt sitzen.
 */
function ZentriereAuf({ ziel, zoom }: { ziel: [number, number] | null; zoom: number }) {
  const map = useMap()
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150)
    return () => clearTimeout(t)
  }, [map])
  useEffect(() => {
    if (ziel) map.setView(ziel, zoom, { animate: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ziel?.[0], ziel?.[1], zoom])
  return null
}

function KlickHandler({ aktiv, onKlick }: { aktiv: boolean; onKlick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: e => { if (aktiv && !messenAktiv(e.target as L.Map)) onKlick(e.latlng.lat, e.latlng.lng) },   // beim Messen setzt nur das Messwerkzeug Punkte
  })
  return null
}

/** Merkt sich die Karteninstanz für Koordinaten-Umrechnung beim Drop. */
function KartenRef({ mapRef }: { mapRef: React.MutableRefObject<L.Map | null> }) {
  const map = useMapEvents({})
  useEffect(() => { mapRef.current = map }, [map, mapRef])
  return null
}

/* ==================================================================== Seite */

export function CoPilotTacticalMapPage() {
  // Die Taktische Karte zeichnet die Führung; der Co-Pilot sieht sie nur an (Pilot-API speichert nicht)
  const coPilotReadOnly = true
  useEffect(() => { if (coPilotReadOnly) { (window as any).CO_PILOT_READ_ONLY = true } }, [coPilotReadOnly])
  // Einsatz aus der globalen Einsatzwahl (Co-Pilot: Einsatz aus dem Kräfte Management, gesperrt)
  const { einsatz: ew, einsatzIdStr: einsatzId, fehler: ewFehler } = useEinsatz()
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([])
  const [zugeordneteFahrzeuge, setZugeordneteFahrzeuge] = useState<number[]>([])
  /** Rohdaten des Kräfte-Managements (Trupps live) und Namen für die Trupp-Info */
  const [kmRoh, setKmRoh] = useState<any>(null)
  const [nutzerNamen, setNutzerNamen] = useState<Record<number, string>>({})
  const [fahrzeugRufe, setFahrzeugRufe] = useState<Record<number, string>>({})

  const [symbole, setSymbole] = useState<KartenSymbol[]>([])
  const [suchgebiete, setSuchgebiete] = useState<Suchgebiet[]>([])
  const [layer, setLayer] = useState<LayerSicht>({ ...LEERE_LAYER })

  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [speicherStatus, setSpeicherStatus] = useState<'leer' | 'speichert' | 'gespeichert' | 'fehler'>('leer')
  const [ladeFehler, setLadeFehler] = useState<string | null>(null)

  /** Aktiver Setz-Modus: null = aus, 'suchgebiet' = Eckpunkte setzen, sonst Symboltyp */
  const [modus, setModus] = useState<string | null>(coPilotReadOnly ? null : null)
  const [geozonTreffer, setGeozonTreffer] = useState<GeozonTreffer[] | null>(null)
  const [geozonOrt, setGeozonOrt] = useState<[number, number] | null>(null)
  const [geozonLaedt, setGeozonLaedt] = useState(false)
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const [dragSymbol, setDragSymbol] = useState<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; x: number; y: number } | null>(null)

  const mapRef = useRef<L.Map | null>(null)
  const datenFehler = useDatenFehler()
  // Live-Telemetrie der über DJI Pilot 2 verbundenen Drohnen
  const { drohnen: liveDrohnen, routen: flugrouten } = useDrohnenLive()
  const pilotMarkierungen = usePilotMarkierungen(einsatzId)
  const abdeckung = useAbdeckung(einsatzId, layer.abdeckungHoehe ?? ABDECKUNG_STANDARD_HOEHE_M, layer.abdeckung === true, suchgebiete.length > 0)
  const [fotoFilter, setFotoFilter] = useState<FotoFilter>('alle')
  /** Messwerkzeuge (Strecke, Fläche, Kreis, Peilung, Koordinate; nur lokal und temporär) */
  const messen = useMessen()
  const fotos = useFotos(einsatzId, layer.fotos === true)
  const hotspots = useHotspots(einsatzId, layer.hotspots === true)
  const medienStatus = useMedienStatus(layer.fotos === true || layer.hotspots === true)
  const hydriert = useRef(false)
  // "Erneut versuchen" (Statusleiste/Banner) lädt die Karte neu
  const [ladeNr, setLadeNr] = useState(0)
  useConnectionRetry(() => setLadeNr(n => n + 1))
  // Die Co-Pilot-Karte ist schreibgeschützt (nichts Ungespeichertes): Wechsel jederzeit erlaubt
  useEinsatzWechsel(() => true)
  /** Zeitpunkt des letzten Ziehens – verhindert Löschen direkt nach dem Verschieben */
  const letztesZiehen = useRef(0)
  const downRef = useRef<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; x: number; y: number; moved: boolean } | null>(null)
  const dragRef = useRef<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; x: number; y: number } | null>(null)

  const einsatz = useMemo<EinsatzOption | null>(() => {
    if (!ew) return null
    const r = ew.raw ?? {}
    return {
      id: ew.id, titel: ew.titel, status: ew.status ?? '', typ: ew.typ ?? '',
      einsatzort_lat: r.einsatzort_lat ?? ew.lat ?? null, einsatzort_lon: r.einsatzort_lon ?? ew.lon ?? null,
      einsatzort_adresse: r.einsatzort_adresse ?? ew.adresse ?? '', beginn_geplant: r.beginn_geplant ?? null,
    }
  }, [ew])
  const einsatzOrt = useMemo<[number, number] | null>(() => {
    const la = einsatz?.einsatzort_lat, lo = einsatz?.einsatzort_lon
    return la != null && lo != null ? [la, lo] : null
  }, [einsatz])

  /* ------------------------------- Karte + zugeteilte Fahrzeuge je Einsatz */

  useEffect(() => {
    if (!einsatzId) {
      setSymbole([]); setSuchgebiete([]); setLayer({ ...LEERE_LAYER })
      setZugeordneteFahrzeuge([]); setFahrzeuge([]); setKmRoh(null); hydriert.current = false; setLadeFehler(null)
      setSpeicherStatus('leer')
      return
    }
    let cancelled = false
    hydriert.current = false; setLadeFehler(null); setLaedt(true); setFahrzeuge([])
    ;(async () => {
      let karte: KarteData | null = null
      for (let versuch = 0; versuch < 3; versuch++) {
        try {
          const [k, km] = await Promise.all([
            api.get(`/taktische-karte/${einsatzId}`),
            api.get(`/kraefte-management/${einsatzId}`),
          ])
          if (cancelled) return
          karte = ((k.data as any)?.data ?? {}) as KarteData
          const d = ((km.data as any)?.data ?? {}) as any
          const ids = new Set<number>()
          for (const b of d.bloecke ?? []) if (b?.fahrzeugId != null) ids.add(Number(b.fahrzeugId))
          setZugeordneteFahrzeuge([...ids])
          setKmRoh(d)
          break
        } catch {
          if (cancelled) return
          if (versuch === 2) setLadeFehler('Kartendaten konnten nicht geladen werden.')
          else await new Promise(r => setTimeout(r, 600))
        }
      }
      if (cancelled) return
      setSymbole(Array.isArray(karte?.symbole) ? (karte!.symbole as KartenSymbol[]) : [])
      setSuchgebiete(Array.isArray(karte?.suchgebiete) ? (karte!.suchgebiete as Suchgebiet[]) : [])
      // Ältere Karten speichern noch `hoehenlinien: boolean` statt der Grundkarte
      const { hoehenlinien, ...gespeichert } = (karte?.layer ?? {}) as Partial<LayerSicht> & { hoehenlinien?: boolean }
      setLayer({
        ...LEERE_LAYER, ...gespeichert,
        grundkarte: gespeichert.grundkarte ?? (hoehenlinien ? 'topo' : 'karte'),
        geozonen: { ...(gespeichert.geozonen ?? {}) },
      })
      hydriert.current = true
      setSpeicherStatus(karte && (karte.symbole?.length || karte.suchgebiete?.length) ? 'gespeichert' : 'leer')
      setLaedt(false)
    })()
    return () => { cancelled = true }
  }, [einsatzId, ladeNr])

  /* --------------------------------------------------------- Autosave */

  /* ------------------- Aktualisieren: Änderungen der Führung alle 10 s nachladen */

  useEffect(() => {
    if (!einsatzId) return
    const t = setInterval(async () => {
      if (!hydriert.current || document.visibilityState === 'hidden') return
      try {
        const k = await api.get(`/taktische-karte/${einsatzId}`)
        const karte = ((k.data as any)?.data ?? {}) as KarteData
        setSymbole(Array.isArray(karte.symbole) ? (karte.symbole as KartenSymbol[]) : [])
        setSuchgebiete(Array.isArray(karte.suchgebiete) ? (karte.suchgebiete as Suchgebiet[]) : [])
        setSpeicherStatus(karte.symbole?.length || karte.suchgebiete?.length ? 'gespeichert' : 'leer')
        setLadeFehler(null)
      } catch {
        // Netzwerk/5xx meldet zusätzlich der Interceptor (Statusleiste); hier der Hinweis, dass die Karte nicht aktuell ist
        setLadeFehler('Karte konnte nicht aktualisiert werden – gezeigt wird der letzte Stand. Nächster Versuch in 10 s.')
      }
    }, 10000)
    return () => clearInterval(t)
  }, [einsatzId])

  // Namen und Fahrzeug-Funkrufnamen für die Trupp-Info (einmal laden)
  useEffect(() => {
    let abbruch = false
    ;(async () => {
      try {
        const [n, f] = await Promise.all([api.get('/nutzer', { params: { size: 500 } }), api.get('/fahrzeuge', { params: { size: 200 } })])
        if (abbruch) return
        const namen: Record<number, string> = {}
        for (const u of ((n.data as any)?.items ?? []) as any[]) namen[u.id] = [u.vorname, u.nachname].filter(Boolean).join(' ') || u.benutzername
        setNutzerNamen(namen)
        const rufe: Record<number, string> = {}
        for (const v of ((f.data as any)?.items ?? []) as any[]) { const r = v.rufzeichen || v.kennzeichen; if (r) rufe[v.id] = r }
        setFahrzeugRufe(rufe)
      } catch { /* Info zeigt dann „Nutzer #id“ */ }
    })()
    return () => { abbruch = true }
  }, [])

  // Trupps aktuell halten (Umbildungen durch die Führung)
  useEffect(() => {
    if (!einsatzId) return
    const t = setInterval(async () => {
      if (document.visibilityState === 'hidden') return
      try { setKmRoh(((await api.get(`/kraefte-management/${einsatzId}`)).data as any)?.data ?? null) } catch { /* nächster Versuch */ }
    }, 10000)
    return () => clearInterval(t)
  }, [einsatzId])

  const trupps = useMemo(() => truppsAuswerten(kmRoh), [kmRoh])
  const truppRufnamen = useMemo(() => fahrzeugRufnamenJeNutzer(kmRoh, id => fahrzeugRufe[id]), [kmRoh, fahrzeugRufe])

  /* --------------------------------------------- Platzieren (Klick/Drop) */

  const platziere = useCallback((typ: string, lat: number, lng: number, kennung?: string, bezeichnung?: string, rufname?: string) => {
    const std = kennung ?? symbolDef(typ)?.standardKennung ?? ''
    setSymbole(prev => [...prev, {
      id: neueId('sym'), typ, lat, lng, kennung: std, bezeichnung,
      ...(rufname ? { rufname } : {}),
    }])
    setModus(null)
  }, [])

  /** Palette-Einträge: taktische Zeichen + die dem Einsatz zugeteilten Fahrzeuge. */
  const palette = useMemo(() => {
    // Mapping: DB fahrzeugtyp -> unser Symbol-Typ
    const fahrzeugTypZuSymbol: Record<string, string> = {
      'ELW': 'elw1',
      'ELW 1': 'elw1',
      'ELW 2': 'elw2',
      'Löschfahrzeug': 'loeschfahrzeug',
      'LF': 'loeschfahrzeug',
      'RTW': 'rtw',
      'Rettungswagen': 'rtw',
      'KTW': 'ktw',
      'Krankentransportwagen': 'ktw',
      'NEF': 'nef',
      'Notarzteinsatzfahrzeug': 'nef',
      'GW-Drohne': 'gw_drohne',
      'GW-Drone': 'gw_drohne',
      'GW-Hundestaffel': 'gw_hundestaffel',
      'GW-Hund': 'gw_hundestaffel',
      'Einsatzfahrzeug': 'einsatzfahrzeug',
      'Abrollbehälter': 'abrollbehaelter',
      'Anhänger': 'anhaenger',
      'Hubschrauber': 'hubschrauber',
      'Wasserfahrzeug': 'wasserfahrzeug',
    }

    const kurzKennung = (f: Fahrzeug, symTyp: string): string => {
      const typText = f.fahrzeugtyp || f.typ || ''
      if (typText && typText.length <= 10) return typText
      return symbolDef(symTyp)?.standardKennung ?? ''
    }

    const holeSymbolTyp = (f: Fahrzeug): string => {
      const keys = [f.fahrzeugtyp, f.typ].filter((v): v is string => Boolean(v))
      for (const key of keys) {
        if (fahrzeugTypZuSymbol[key]) return fahrzeugTypZuSymbol[key]
        // Teilstring-Match (z. B. "GW-Drohne" in "GW-Drohne 56-79-1")
        for (const [dbTyp, symTyp] of Object.entries(fahrzeugTypZuSymbol)) {
          if (key.includes(dbTyp)) return symTyp
        }
      }
      return 'fahrzeug'  // Fallback
    }

    const fzg = zugeordneteFahrzeuge
      .map((id) => fahrzeuge.find(x => x.id === id))
      .filter((v): v is Fahrzeug => Boolean(v))
      .map((f) => {
        const symTyp = holeSymbolTyp(f)
        return {
          typ: symTyp,
          // Im Zeichen steht der Fahrzeugtyp (kurz), das Rufzeichen steht als Rufname darunter.
          label: [f.fahrzeugtyp || f.typ || 'Fahrzeug', f.rufzeichen || f.kennzeichen || `#${f.id}`]
            .filter(Boolean).join(' · '),
          kennung: kurzKennung(f, symTyp),
          rufname: f.rufzeichen || f.kennzeichen || '',
          bezeichnung: f.fahrzeugtyp || f.typ || 'Fahrzeug',
          group: 'Zugeteilte Fahrzeuge',
        }
      })

    return [
      ...fzg,
      ...SYMBOL_KATALOG.map(s => ({ typ: s.typ, label: s.label, kennung: s.standardKennung ?? '', bezeichnung: undefined as string | undefined, rufname: undefined as string | undefined, group: s.group })),
    ]  }, [zugeordneteFahrzeuge, fahrzeuge])

  /* ------------------------------------------- Pointer-Drag aus der Leiste */

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = downRef.current
      if (!d) return
      if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 8) return
      d.moved = true
      const next = { typ: d.typ, kennung: d.kennung, bezeichnung: d.bezeichnung, rufname: d.rufname, x: e.clientX, y: e.clientY }
      dragRef.current = next
      setDragSymbol(next)
    }
    const onUp = (e: PointerEvent) => {
      const d = downRef.current
      const cur = dragRef.current
      downRef.current = null; dragRef.current = null; setDragSymbol(null)
      if (!d?.moved || !cur) return
      const map = mapRef.current
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const karte = el?.closest('.leaflet-container') as HTMLElement | null
      if (!map || !karte) return
      const r = karte.getBoundingClientRect()
      const ll = map.containerPointToLatLng(L.point(e.clientX - r.left, e.clientY - r.top))
      platziere(cur.typ, ll.lat, ll.lng, cur.kennung, cur.bezeichnung, cur.rufname)
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [platziere])

  /* ------------------------------------------------------------ Aktionen */

  const verschiebeSymbol = (id: string, lat: number, lng: number) =>
    setSymbole(p => p.map(s => (s.id === id ? { ...s, lat, lng } : s)))
  const loescheSymbol = (id: string) => {
    setSymbole(p => p.filter(s => s.id !== id)); setBearbeitet(null)
  }
  const aendereSymbol = (id: string, patch: Partial<TaktikZeichen>) =>
    setSymbole(p => p.map(s => (s.id === id ? { ...s, ...patch } : s)))

  /** Geozonen im Umfeld des Klickpunkts über WFS abfragen (±150 m). */
  const frageGeozonenAb = useCallback(async (lat: number, lng: number) => {
    const aktive = GEOZONEN.filter(g => layer.luftraum && layer.geozonen[g.key])
    setGeozonOrt([lat, lng])
    if (aktive.length === 0) { setGeozonTreffer([]); return }
    setGeozonLaedt(true)
    const d = 0.0015                                  // ca. 150 m
    const bbox = `${lng - d},${lat - d},${lng + d},${lat + d},EPSG:4326`   // lon,lat!
    const treffer: GeozonTreffer[] = []
    let abfrageFehler = false
    await Promise.all(aktive.map(async gz => {
      try {
        const r = await fetch(
          `${DIPUL_OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=dipul:${gz.layer}` +
          `&count=5&outputFormat=application/json&srsName=EPSG:4326&bbox=${bbox}`,
        )
        if (!r.ok) { abfrageFehler = true; return }
        const j = await r.json()
        for (const f of j.features ?? []) {
          const p: any = f.properties ?? {}
          const ug = p.lower_limit_altitude != null
            ? `${p.lower_limit_altitude} ${p.lower_limit_unit ?? ''} ${p.lower_limit_alt_ref ?? ''}`.trim() : ''
          const og = p.upper_limit_altitude != null
            ? `${p.upper_limit_altitude} ${p.upper_limit_unit ?? ''} ${p.upper_limit_alt_ref ?? ''}`.trim() : ''
          treffer.push({
            zone: gz.label,
            farbe: gz.farbe,
            name: p.generated_name_DE || p.name || '—',
            kategorie: p.type_code_detail || p.type_code || '—',
            rechtsgrundlage: p.legal_ref ?? '—',
            untergrenze: ug, obergrenze: og,
            gueltigkeit: p.start_time && p.end_time
              ? `${new Date(p.start_time).toLocaleString('de-DE')} – ${new Date(p.end_time).toLocaleString('de-DE')}` : '',
          })
        }
      } catch { abfrageFehler = true }
    }))
    if (abfrageFehler) setFehler('Geozonen-Abfrage (DiPUL) war nicht vollständig möglich – das Ergebnis kann unvollständig sein.')
    setGeozonTreffer(treffer)                          // Doppelte zusammenfassen
    setGeozonLaedt(false)
  }, [layer.luftraum, layer.geozonen])

  const klickAufKarte = (lat: number, lng: number) => {
    if (modus === 'suchgebiet') {
      setSuchgebiete(prev => {
        if (prev.length === 0) {
          return [{ id: neueId('sg'), kennung: `Suchgebiet ${prev.length + 1}`, punkte: [[lat, lng]] }]
        }
        const letztes = prev[prev.length - 1]
        return [...prev.slice(0, -1), { ...letztes, punkte: [...letztes.punkte, [lat, lng]] }]
      })
} else if (modus && !coPilotReadOnly) {
      const pe = palette.find(p => p.typ === modus)
      platziere(modus, lat, lng, pe?.kennung || undefined, pe?.bezeichnung, pe?.rufname)
    } else {
      void frageGeozonenAb(lat, lng)
    }
  }

  const verschiebeEckpunkt = (sgId: string, idx: number, lat: number, lng: number) =>
    setSuchgebiete(p => p.map(sg => sg.id !== sgId ? sg : {
      ...sg, punkte: sg.punkte.map((pt, i) => (i === idx ? [lat, lng] as [number, number] : pt)),
    }))
  const loescheEckpunkt = (sgId: string, idx: number) =>
    setSuchgebiete(p => p.flatMap(sg => {
      if (sg.id !== sgId) return [sg]
      const punkte = sg.punkte.filter((_, i) => i !== idx)
      return punkte.length >= 2 ? [{ ...sg, punkte }] : []
    }))
  const loescheGebiet = (sgId: string) => setSuchgebiete(p => p.filter(sg => sg.id !== sgId))
  const neuesGebiet = () => {
    // Bewusst ohne Startpunkt: alle Eckpunkte werden selbst gesetzt.
    // Noch leere Gebiete (Zeichnen ohne Klick auf die Karte beendet) werden dabei verworfen.
    setSuchgebiete(p => {
      const belegt = p.filter(sg => sg.punkte.length > 0)
      return [...belegt, { id: neueId('sg'), kennung: `Suchgebiet ${belegt.length + 1}`, punkte: [] }]
    })
    setModus('suchgebiet')
  }
  // Zeichnen beenden bzw. starten; beim Beenden leere Gebiete entfernen
  const suchgebietUmschalten = () => {
    if (modus === 'suchgebiet') {
      setModus(null)
      setSuchgebiete(p => p.filter(sg => sg.punkte.length > 0))
    } else {
      neuesGebiet()
    }
  }

  const icon = (s: KartenSymbol, aktiv = false) => divIcon({
    html: symbolIconHtml(s, aktiv, layer.rufnamen !== false),
    className: 'taktik-symbol',
    iconSize: [52, 40],
    iconAnchor: [26, 20],
  })

  const mitte: [number, number] = einsatzOrt ?? [49.8728, 8.6512]

  /* ------------------------------------------------------------------ UI */

  // Höhenlinien bewusst NICHT hier – sie stehen als letzter Schalter unten.
  const layerSchalter: Array<{ key: 'einsatzort' | 'symbole' | 'rufnamen' | 'suchgebiete' | 'flugrouten' | 'pilotmarker'; label: string }> = [
    { key: 'einsatzort', label: 'Einsatzort (roter Punkt)' },
    { key: 'symbole', label: 'Taktische Symbole' },
    { key: 'rufnamen', label: 'Rufnamen unter den Zeichen' },
    { key: 'suchgebiete', label: 'Suchgebiete' },
    { key: 'flugrouten', label: 'Flugrouten (aktueller Flug)' },
    { key: 'pilotmarker', label: 'Markierungen aus DJI Pilot 2' },
  ]

  const grundkarteWahl = (
    <div className="mb-2 flex overflow-hidden rounded-xl border border-dark-border">
      {GRUNDKARTEN.map(g => (
        <button key={g.key} type="button" aria-pressed={layer.grundkarte === g.key}
          onClick={() => setLayer(v => ({ ...v, grundkarte: g.key }))}
          className={`min-h-[44px] flex-1 px-2 text-xs font-semibold transition-colors ${layer.grundkarte === g.key
            ? 'bg-primary-500 text-white'
            : 'text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text'}`}>
          {g.label}
        </button>
      ))}
    </div>
  )

  const panels: KartenPanelDef[] = [
    {
      id: 'ebenen', titel: 'Ebenen', icon: <Layers className="h-4 w-4" />, seite: 'links', startOffen: true,
      inhalt: (
        <>
          {grundkarteWahl}
                <div className="space-y-1">
                  {layerSchalter.map(l => (
                    <label key={l.key}
                      className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm hover:bg-dark-bg-tertiary">
                      <input type="checkbox" checked={layer[l.key] !== false}
                        onChange={e => setLayer(v => ({ ...v, [l.key]: e.target.checked }))}
                        className="h-5 w-5 rounded border-dark-border accent-primary-500" />
                      <span className="flex-1 text-dark-text">{l.label}</span>
                    </label>
                  ))}

                  {/* Abdeckungsnachweis: Anteil der Suchgebiete, den die Kamera gesehen hat */}
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm hover:bg-dark-bg-tertiary">
                    <input type="checkbox" checked={layer.abdeckung === true}
                      onChange={e => setLayer(v => ({ ...v, abdeckung: e.target.checked }))}
                      className="h-5 w-5 rounded border-dark-border accent-primary-500" />
                    <span className="flex-1 text-dark-text">Abdeckung (von der Kamera gesehen)</span>
                  </label>
                  {layer.abdeckung === true && (
                    <div className="px-2 pb-1">
                      <label className="flex items-center gap-2 text-xs text-dark-text-subtle">
                        zählt bis Höhe über Grund
                        <b className="text-dark-text">{layer.abdeckungHoehe ?? ABDECKUNG_STANDARD_HOEHE_M} m</b>
                        <span>(Einstellung der Führung)</span>
                      </label>
                      <AbdeckungHinweis daten={abdeckung} />
                    </div>
                  )}

                  {/* Foto-Lagebild und Hotspots (Fotos aus DJI Pilot 2) */}
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm hover:bg-dark-bg-tertiary">
                    <input type="checkbox" checked={layer.fotos === true}
                      onChange={e => setLayer(v => ({ ...v, fotos: e.target.checked }))}
                      className="h-5 w-5 rounded border-dark-border accent-primary-500" />
                    <span className="flex-1 text-dark-text">Lagebild (Fotos)</span>
                  </label>
                  {layer.fotos === true && (
                    <div className="flex gap-1 px-2 pb-1">
                      {([['alle', 'Alle'], ['weit', 'Weitwinkel'], ['ir', 'IR'], ['pano', 'Pano']] as const).map(([k, t]) => (
                        <button key={k} type="button" aria-pressed={fotoFilter === k} onClick={() => setFotoFilter(k)}
                          className={`min-h-[36px] flex-1 rounded-lg border px-2 text-xs font-semibold ${fotoFilter === k ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-subtle'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm hover:bg-dark-bg-tertiary">
                    <input type="checkbox" checked={layer.hotspots === true}
                      onChange={e => setLayer(v => ({ ...v, hotspots: e.target.checked }))}
                      className="h-5 w-5 rounded border-dark-border accent-primary-500" />
                    <span className="flex-1 text-dark-text">Hotspots (IR-Fotos)</span>
                  </label>
                  {(layer.fotos === true || layer.hotspots === true) && <MedienStatusHinweis status={medienStatus} />}

                  {/* DIPUL-Geozonen nach §21h LuftVO */}
                  <label className="mt-1 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm hover:bg-dark-bg-tertiary">
                    <input type="checkbox" checked={layer.luftraum}
                      onChange={e => {
                        const an = e.target.checked
                        setLayer(v => ({
                          ...v, luftraum: an,
                          geozonen: an && Object.values(v.geozonen).every(x => !x)
                            ? { flugbeschraenkung: true, temporaer: true, kontrollzone: true }
                            : v.geozonen,
                        }))
                      }}
                      className="h-5 w-5 rounded border-dark-border accent-primary-500" />
                    <span className="flex-1 text-dark-text">Luftraum / Schutzgebiete</span>
                    <span className="text-[10px] text-dark-text-subtle">DIPUL · §21h LuftVO</span>
                  </label>

                  {layer.luftraum && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l border-dark-border pl-3">
                      {GEOZONEN.map(gz => (
                        <label key={gz.key} className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2 text-[13px] hover:bg-dark-bg-tertiary">
                          <input type="checkbox" checked={!!layer.geozonen[gz.key]}
                            onChange={e => {
                              const next = { ...layer, geozonen: { ...layer.geozonen, [gz.key]: e.target.checked } }
                              setLayer(next)
                            }}
                            className="h-4 w-4 rounded border-dark-border accent-primary-500" />
                          <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: gz.farbe }} />
                          <span className="text-dark-text">{gz.label}</span>
                        </label>
                      ))}
                      <p className="px-2 pt-1 text-[10px] leading-tight text-dark-text-subtle">
                        Daten: © DFS/DIPUL – Geozonen nach §21h LuftVO (kostenfrei).
                        Klick auf die Karte zeigt Details im Umfeld.
                      </p>
                    </div>
                  )}

                </div>
        </>
      ),
    },
    {
      id: 'messen', titel: 'Messen', icon: <Ruler className="h-4 w-4" />, seite: 'rechts', badge: messen.art ? 'aktiv' : undefined,
      inhalt: ({ einklappen }) => <MessPanelInhalt messen={messen} einklappen={einklappen} />,
    },
    {
      id: 'drohnen', titel: 'Drohnen live', icon: <Crosshair className="h-4 w-4" />, seite: 'rechts', startOffen: true, badge: liveDrohnen.length || undefined,
      inhalt: (
        <DrohnenPanel drohnen={liveDrohnen}
          onZentrieren={(lat, lon) => mapRef.current?.flyTo([lat, lon], Math.max(mapRef.current.getZoom(), 16))} />
      ),
    },
    {
      id: 'gebiete', titel: 'Gebiete & Übersicht', icon: <MapIcon className="h-4 w-4" />, seite: 'rechts',
      badge: symbole.length + suchgebiete.length || undefined,
      inhalt: (
        <div className="text-xs">
          {!symbole.length && !suchgebiete.length && <p className="text-dark-text-subtle">Noch keine Zeichen oder Suchgebiete gesetzt.</p>}
                {suchgebiete.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {suchgebiete.map(sg => (
                      <div key={sg.id} className="flex flex-wrap items-center gap-2">
                        <Crosshair className="h-3.5 w-3.5 text-amber-400" />
                        {coPilotReadOnly ? (
                          <span className="text-dark-text">{sg.kennung}</span>
                        ) : (
                          <Input value={sg.kennung} className="h-9 max-w-[220px]"
                            onChange={e => setSuchgebiete(p => p.map(x => x.id === sg.id ? { ...x, kennung: e.target.value } : x))} />
                        )}
                        <span className="text-dark-text-subtle">{sg.punkte.length} Eckpunkte</span>
                        {sg.punkte.length >= 3 && <span className="font-semibold text-amber-300" data-testid="suchgebiet-flaeche">{geodaesie.textFlaecheStufen(geodaesie.flaecheM2(sg.punkte))}</span>}
                        {abdeckungProzent(abdeckung, sg.id) !== null && (
                          <span className="font-semibold text-green-400" title="Anteil des Suchgebiets, den die Kamera gesehen hat">
                            {abdeckungProzent(abdeckung, sg.id)} % abgedeckt
                          </span>
                        )}
                        {!coPilotReadOnly && (
                          <button className="flex h-9 w-9 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10"
                            onClick={() => loescheGebiet(sg.id)} title="Suchgebiet löschen">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-dark-text-subtle">
                  <span>{symbole.length} Symbol(e)</span>
                  <span>·</span>
                  <span>{suchgebiete.reduce((n, s) => n + s.punkte.length, 0)} Suchgebiet-Eckpunkte</span>
                  <span>·</span>
                  <span>{coPilotReadOnly ? 'Wird von der Führung gezeichnet · aktualisiert alle 10 s · Zeichen antippen für Infos' : 'Zeichen verschieben: ziehen · Infos und Bearbeiten: anklicken bzw. antippen'}</span>
                </div>
        </div>
      ),
    },
    {
      id: 'geozonen', titel: 'Geozonen im Umfeld', icon: <MapIcon className="h-4 w-4" />, seite: 'rechts', verbergen: !geozonOrt, oeffnenBei: geozonOrt,
      inhalt: geozonOrt && (
        <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-xs font-semibold text-dark-text">
                      <MapIcon className="h-3.5 w-3.5 text-primary-400" /> Geozonen im Umfeld
                    </span>
                    <button onClick={() => { setGeozonOrt(null); setGeozonTreffer(null) }} aria-label="Schließen"
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mb-2 text-[10px] text-dark-text-subtle">
                    ca. ±150 m um {geozonOrt[0].toFixed(5)}, {geozonOrt[1].toFixed(5)}
                  </p>
                  {geozonLaedt ? (
                    <p className="text-xs text-dark-text-muted">Frage DIPUL ab…</p>
                  ) : !layer.luftraum ? (
                    <p className="text-xs text-dark-text-subtle">
                      Layer „Luftraum / Schutzgebiete" ist ausgeschaltet.
                    </p>
                  ) : (geozonTreffer?.length ?? 0) === 0 ? (
                    <p className="text-xs text-dark-text-subtle">Keine Geozone im Umfeld.</p>
                  ) : (
                    <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
                      {geozonTreffer!.map((t, i) => (
                        <div key={i} className="rounded-lg border border-dark-border bg-dark-bg-tertiary/60 p-2">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: t.farbe }} />
                            <span className="text-[11px] text-dark-text-subtle">{t.zone}</span>
                          </div>
                          <div className="mt-0.5 text-sm font-semibold text-dark-text">{t.name}</div>
                          <dl className="mt-1 space-y-0.5 text-[11px] text-dark-text-muted">
                            <div className="flex gap-1"><span className="text-dark-text-subtle">Kategorie:</span> <span>{t.kategorie}</span></div>
                            <div className="flex gap-1"><span className="text-dark-text-subtle">Rechtsgrundlage:</span> <span>{t.rechtsgrundlage}</span></div>
                            {(t.untergrenze || t.obergrenze) && (
                              <div className="flex gap-1">
                                <span className="text-dark-text-subtle">Höhe:</span>
                                <span>{t.untergrenze || '—'} bis {t.obergrenze || '—'}</span>
                              </div>
                            )}
                            {t.gueltigkeit && (
                              <div className="flex gap-1"><span className="text-dark-text-subtle">Gültig:</span> <span>{t.gueltigkeit}</span></div>
                            )}
                          </dl>
                        </div>
                      ))}
                    </div>
                  )}
        </div>
      ),
    },
  ]

  return (
    <div className="-m-2 flex ui-fill min-h-[420px] flex-col gap-2 sm:-m-3">
      {fehler && (
        <div className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">{fehler}</div>
      )}

      {(ladeFehler || ewFehler || datenFehler.length > 0) && (
        <div className="shrink-0 space-y-1 rounded-xl border border-dark-border bg-dark-bg-secondary/60 px-3 py-1.5">
          {(ladeFehler || ewFehler) && <p className="text-xs text-red-300" role="alert">{ladeFehler || ewFehler}</p>}
          {datenFehler.length > 0 && <p className="text-xs text-amber-300" role="alert">Nicht aktualisiert: {datenFehler.join(', ')} – letzte bekannte Daten werden angezeigt.</p>}
        </div>
      )}

      {!einsatzId ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <KeinEinsatzLeer art="karte" />
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <KartenBuehne seitenKey="pilot-taktische-karte" mapRef={mapRef} panels={panels}>
              <MapContainer center={mitte} zoom={einsatzOrt ? 14 : 12} maxZoom={20} className="h-full w-full" zoomControl={false}>
                <ZoomControl position="bottomright" />
                <KartenRef mapRef={mapRef} />
                <ZentriereAuf ziel={einsatzOrt} zoom={einsatzOrt ? 15 : 12} />
                <KlickHandler aktiv={true} onKlick={klickAufKarte} />
                <MessLayer messen={messen} />

                {(() => {
                  const gk = GRUNDKARTEN.find(g => g.key === layer.grundkarte) ?? GRUNDKARTEN[0]
                  // key erzwingt einen neuen TileLayer – react-leaflet tauscht die url sonst ohne Attribution
                  return <TileLayer key={gk.key} url={gk.url} attribution={gk.attribution}
                    maxNativeZoom={gk.maxNativeZoom} maxZoom={20} />
                })()}

                {/* DIPUL-Geozonen (§21h LuftVO) als WMS-Overlays */}
                {layer.luftraum && GEOZONEN.filter(gz => layer.geozonen[gz.key]).map(gz => (
                  <WmsGeozon key={gz.key} layer={gz.layer} opacity={0.75} />
                ))}

                {/* Rote Markierung am Einsatzort */}
                {layer.einsatzort && einsatzOrt && (
                  <Marker position={einsatzOrt} icon={divIcon({
                    html: `<div style="width:18px;height:18px;border-radius:9999px;background:#dc2626;border:3px solid #fff;box-shadow:0 0 0 3px rgba(220,38,38,.45)"></div>`,
                    className: '', iconSize: [18, 18], iconAnchor: [9, 9],
                  })}>
                    <Tooltip direction="top" offset={[0, -12]}>
                      {einsatz?.titel} — Einsatzort
                    </Tooltip>
                  </Marker>
                )}

                {/* Suchgebiete */}
                {layer.suchgebiete && suchgebiete.map(sg => (
                  <Polygon key={sg.id} positions={sg.punkte}
                    pathOptions={{ color: '#f59e0b', weight: 2, fillOpacity: 0.12, dashArray: '6 4' }}>
                    {sg.punkte.length >= 3 && (
                      <Tooltip sticky>{sg.kennung} · {geodaesie.textFlaecheStufen(geodaesie.flaecheM2(sg.punkte))}
                        {layer.abdeckung === true && abdeckungProzent(abdeckung, sg.id) !== null && <> · {abdeckungProzent(abdeckung, sg.id)} % abgedeckt</>}
                      </Tooltip>
                    )}
                  </Polygon>
                ))}
                {layer.abdeckung === true && <AbdeckungLayer daten={abdeckung} />}
                {layer.fotos === true && <FotoLayer fotos={fotos} filter={fotoFilter} />}
                {layer.hotspots === true && <HotspotLayer hotspots={hotspots} />}

                {/* Eckpunkte: ziehen = verschieben, Klick = löschen */}
                {layer.suchgebiete && suchgebiete.flatMap(sg => sg.punkte.map((pt, i) => (
                  <Marker key={`${sg.id}-${i}`} position={pt} draggable={!coPilotReadOnly}
                    eventHandlers={{
                      dragend: e => {
                        letztesZiehen.current = Date.now()
                        const p = (e.target as L.Marker).getLatLng()
                        verschiebeEckpunkt(sg.id, i, p.lat, p.lng)
                      },
                      click: () => {
                        // Sicherheitsnetz: unmittelbar nach dem Verschieben nicht löschen
                        if (coPilotReadOnly || Date.now() - letztesZiehen.current < 350) return
                        loescheEckpunkt(sg.id, i)
                      },
                    }}
                    icon={divIcon({
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:9999px;background:#f59e0b;color:#111827;font:700 12px Arial;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);cursor:pointer">${i + 1}</div>`,
                      className: '', iconSize: [26, 26], iconAnchor: [13, 13],
                    })}>
                    <Tooltip direction="top">
                      {sg.kennung} · Punkt {i + 1}{!coPilotReadOnly && <><br />ziehen = verschieben · Klick = löschen</>}
                    </Tooltip>
                  </Marker>
                )))}

                {/* Taktische Symbole */}
                {layer.symbole && symbole.map(s => (
                  <Marker key={s.id} position={[s.lat, s.lng]} draggable={!coPilotReadOnly} icon={icon(truppZeichen(s, trupps), bearbeitet === s.id)}
                    eventHandlers={{
                      dragend: e => { const p = (e.target as L.Marker).getLatLng(); verschiebeSymbol(s.id, p.lat, p.lng) },
                      click: () => setBearbeitet(b => (b === s.id ? null : s.id)),
                    }}>
                    {hatMaus && bearbeitet !== s.id && (
                      <Tooltip direction="top" offset={[0, -14]}>
                        <SymbolTooltip s={truppZeichen(s, trupps)} trupp={truppAnzeige(s, trupps, nutzerNamen, truppRufnamen)} />
                      </Tooltip>
                    )}
                  </Marker>
                ))}
                {layer.flugrouten !== false && <FlugroutenLayer routen={flugrouten} drohnen={liveDrohnen} />}
                {layer.pilotmarker !== false && <PilotMarkierungenLayer markierungen={pilotMarkierungen} />}
                <DrohnenLayer drohnen={liveDrohnen} />
              </MapContainer>

              {bearbeitet && (() => {
                const s = symbole.find(x => x.id === bearbeitet)
                if (!s) return null
                return (
                  <SymbolInfoKarte key={s.id} positionCls="left-1/2 top-3 z-[34] -translate-x-1/2" s={truppZeichen(s, trupps)} trupp={truppAnzeige(s, trupps, nutzerNamen, truppRufnamen)} nurLesen={coPilotReadOnly}
                    onAendern={patch => aendereSymbol(s.id, patch)}
                    onLoeschen={() => loescheSymbol(s.id)}
                    onSchliessen={() => setBearbeitet(null)} />
                )
              })()}
          </KartenBuehne>
        </div>
      )}

      {/* Drag-Ghost */}
      {dragSymbol && (
        <div className="pointer-events-none fixed z-[9999] opacity-90"
          style={{ left: dragSymbol.x - 26, top: dragSymbol.y - 20 }}>
          <span dangerouslySetInnerHTML={{ __html: symbolSvg(dragSymbol.typ, dragSymbol.kennung) }} />
        </div>
      )}
    </div>
  )
}
